/**
 * MarketplaceUpdatesPage — /marketplace/updates
 * Updates to what the organization acquired, in a table with a versions sheet. Demo pages: /tables/data-tables (rows,
 * badges, row actions) and /pricing-tables. Template example: NextAdmin
 * `(dashboard)/(home)/_component/last-transactions-table`. Moving a version is `CanInstallMarketplace` (the policy of
 * updateMarketplaceEntity and applyMarketplaceUpdates).
 *
 * An acquired entity stays on the version it was acquired at; this lists the ones whose publisher has released
 * different content since, with what changed, and moves them: one, all, or back to an earlier version. A row that was
 * just moved stays on screen (in `moved`) so the way back is one click away instead of vanishing with the refetch.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changedPartLabel } from '@confluent/shared/lib/marketplace/changed-part-label';
import { AlertTriangle, ArrowUpCircle, CheckCircle2, History } from 'lucide-react';
import {
  applyMarketplaceUpdatesMutation,
  getMarketplaceEntityVersionsOptions,
  listMarketplaceInstallationsQueryKey,
  listMarketplaceNeedsSetupQueryKey,
  listMarketplaceUpdatesOptions,
  listMarketplaceUpdatesQueryKey,
  updateMarketplaceEntityMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type {
  MarketplaceAcquisition,
  MarketplaceAcquisitionMove,
  MarketplaceAcquisitionVersion,
} from '../../generated/api/types.gen.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Callout } from '../../components/ui/Callout.js';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Sheet } from '../../components/ui/Sheet.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { useToast } from '../../components/ui/Toast.js';
import { PAGE_HELP } from '../../help/index.js';
import { marketplaceEntityLabel } from './marketplaceLabels.js';

type Row = { kind: 'pending'; update: MarketplaceAcquisition } | { kind: 'moved'; move: MarketplaceAcquisitionMove };

const rowEntityId = (row: Row) => (row.kind === 'pending' ? row.update.entityId : row.move.entityId);
const note = (version: MarketplaceAcquisitionVersion) => version.note?.trim() || 'No note from the publisher';

/** Every published version of one acquired entity, with the button that moves to it. */
function VersionsSheet({
  update,
  moving,
  onMove,
  onClose,
}: {
  update: MarketplaceAcquisition | null;
  moving: boolean;
  onMove: (entityId: number, toVersion: number) => void;
  onClose: () => void;
}) {
  const query = useQuery({
    ...getMarketplaceEntityVersionsOptions({ path: { entityId: update?.entityId ?? 0 } }),
    enabled: update !== null,
  });
  const changedParts = query.data?.changedParts ?? [];

  return (
    <Sheet
      open={update !== null}
      onClose={onClose}
      title={update?.name ?? 'Versions'}
      description="Published versions"
      width="md"
    >
      {query.isPending && (
        <div className="space-y-3" role="status" aria-label="Loading versions">
          <Skeleton height={44} rounded="rounded-lg" />
          <Skeleton height={44} rounded="rounded-lg" />
        </div>
      )}
      {query.isError && (
        <Callout tone="danger" title="Could not load the published versions">
          {query.error.message}
        </Callout>
      )}
      {update && query.data && (
        <div className="space-y-4">
          {changedParts.length > 0 && (
            <p className="text-sm text-body">
              <span className="font-medium text-heading">Parts that changed:</span>{' '}
              {changedParts.map(changedPartLabel).join(', ')}
            </p>
          )}
          <ul className="divide-y divide-card-border rounded-lg border border-card-border">
            {(query.data.versions ?? []).map((version) => (
              <li key={version.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0 text-sm">
                  <span className="font-medium text-heading">Version {version.version}</span>
                  <p className="text-xs text-muted">{note(version)}</p>
                </div>
                {version.version === update.held.version ? (
                  <Badge variant="neutral">You run this</Badge>
                ) : (
                  <Can policy="CanInstallMarketplace">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={moving}
                      onClick={() => onMove(update.entityId, version.version)}
                    >
                      Use version {version.version}
                    </Button>
                  </Can>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Sheet>
  );
}

export const MarketplaceUpdatesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [versionsFor, setVersionsFor] = useState<MarketplaceAcquisition | null>(null);
  const [moved, setMoved] = useState<Map<number, MarketplaceAcquisitionMove>>(new Map());
  const [failed, setFailed] = useState<Array<{ entityId: number; name: string; reason: string }>>([]);

  const query = useQuery(listMarketplaceUpdatesOptions());

  const remember = async (moves: MarketplaceAcquisitionMove[]) => {
    setMoved((previous) => {
      const next = new Map(previous);
      for (const move of moves) next.set(move.entityId, move);
      return next;
    });
    setVersionsFor(null);
    // Held lists on other pages read their own keys: mark all stale, refetch the ones this page feeds.
    await queryClient.invalidateQueries({ refetchType: 'none' });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: listMarketplaceUpdatesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: listMarketplaceInstallationsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: listMarketplaceNeedsSetupQueryKey() }),
    ]);
  };

  const moveOne = useMutation({
    ...updateMarketplaceEntityMutation(),
    onSuccess: (move) => remember([move]),
    onError: (error) => notify(error.message, 'danger'),
  });
  const moveAll = useMutation({
    ...applyMarketplaceUpdatesMutation(),
    onSuccess: async (result) => {
      setFailed(result.failed);
      await remember(result.updated);
      notify(`${result.updated.length} updated.`);
    },
    onError: (error) => notify(error.message, 'danger'),
  });

  const updates = query.data?.data ?? [];
  const pendingIds = new Set(updates.map((update) => update.entityId));
  const rows: Row[] = [
    ...updates.map((update): Row => ({ kind: 'pending', update })),
    ...Array.from(moved.values())
      .filter((move) => !pendingIds.has(move.entityId))
      .map((move): Row => ({ kind: 'moved', move })),
  ];
  const busy = moveOne.isPending || moveAll.isPending;
  const help = PAGE_HELP['marketplace-updates'];
  const moveTo = (entityId: number, toVersion?: number) => moveOne.mutate({ path: { entityId }, body: { toVersion } });

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'name',
      header: 'Entity',
      width: '24%',
      render: (row) => {
        const name = row.kind === 'pending' ? row.update.name : row.move.name;
        const type = row.kind === 'pending' ? row.update.type : row.move.type;
        return (
          <div>
            <p className="text-[13px] font-semibold text-heading">{name}</p>
            <p className="mt-[3px] text-[11px] text-muted">{marketplaceEntityLabel(type)}</p>
          </div>
        );
      },
    },
    {
      key: 'version',
      header: 'Version',
      render: (row) =>
        row.kind === 'pending' ? (
          <Badge variant="warning">
            Version {row.update.held.version} to {row.update.latest.version}
          </Badge>
        ) : (
          <Badge variant="success">Now on version {row.move.to.version}</Badge>
        ),
    },
    {
      key: 'changes',
      header: 'What changed',
      width: '38%',
      render: (row) =>
        row.kind === 'pending' ? (
          <ul className="space-y-1">
            {row.update.changes.map((change) => (
              <li key={change.id} className="text-xs text-body">
                <span className="font-medium text-heading">Version {change.version}</span>
                <span className="text-muted">: {note(change)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-muted">Moved just now</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '260px',
      render: (row) =>
        row.kind === 'pending' ? (
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setVersionsFor(row.update)}>
              <History size={14} aria-hidden="true" />
              Versions
            </Button>
            <Can policy="CanInstallMarketplace">
              <Button size="sm" disabled={busy} onClick={() => moveTo(row.update.entityId)}>
                <ArrowUpCircle size={14} aria-hidden="true" />
                Update
              </Button>
            </Can>
          </div>
        ) : (
          <Can policy="CanInstallMarketplace">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => moveTo(row.move.entityId, row.move.from.version)}
            >
              <History size={14} aria-hidden="true" />
              Back to version {row.move.from.version}
            </Button>
          </Can>
        ),
    },
  ];

  return (
    <ListPage
      title={updates.length > 0 ? `Updates (${updates.length})` : 'Updates'}
      subtitle="What you acquired from the marketplace that its publisher has changed since."
      helpContent={help}
      breadcrumbs={[{ label: 'Marketplace', to: '/marketplace' }]}
      actions={
        <>
          <Link to="/marketplace" className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}>
            Back to marketplace
          </Link>
          {updates.length > 1 && (
            <Can policy="CanInstallMarketplace">
              <Button
                size="sm"
                disabled={busy}
                loading={moveAll.isPending}
                onClick={() => moveAll.mutate({ body: {} })}
              >
                <ArrowUpCircle size={14} aria-hidden="true" />
                Update all
              </Button>
            </Can>
          )}
        </>
      }
      alert={
        query.isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load updates"
            actions={
              <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            {query.error.message}
          </Callout>
        ) : failed.length > 0 ? (
          <Callout
            tone="warning"
            icon={AlertTriangle}
            title="Some entities could not be updated and stayed where they were"
          >
            {failed.map((failure) => (
              <p key={failure.entityId}>
                {failure.name}: {failure.reason}
              </p>
            ))}
          </Callout>
        ) : undefined
      }
    >
      {!query.isError && (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={rowEntityId}
          loading={query.isPending}
          aria-label="Marketplace updates"
          empty={
            <EmptyState
              icon={CheckCircle2}
              title={help?.emptyState.title ?? 'Everything is up to date'}
              description={
                help?.emptyState.description ??
                'Nothing this organization acquired has been changed by its publisher since.'
              }
              learnItems={help?.emptyState.learnItems}
            />
          }
        />
      )}
      <VersionsSheet
        update={versionsFor}
        moving={busy}
        onMove={(entityId, toVersion) => moveTo(entityId, toVersion)}
        onClose={() => setVersionsFor(null)}
      />
    </ListPage>
  );
};
